import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";
import OverviewRegister from "./OverviewRegister";
import OrganizationChart from "./OrganizationChart";

const OverviewView = ({ is_superuser }) => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  // 요청 전 상태(모름)과 요청 중(loading)을 구분
  const [overview, setOverview] = useState(undefined); // undefined = 아직 모름
  const [loading, setLoading] = useState(true);
  const [canManageOverview, setCanManageOverview] = useState(false);

  const token = localStorage.getItem("access");

  useEffect(() => {
    let mounted = true;
    // 토큰/프로젝트 없을 때도 로딩 종료는 확실히
    if (!projectId || !token) {
      setOverview(null);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await api.get(`project/${projectId}/overview/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) {
          const data = res.data || null;
          setCanManageOverview(Boolean(data?.can_manage_overview));
          // console.log("API 응답 원본:", data);
          const raw = data?.overview ?? null;
          // console.log("overview.raw:", raw);
          const meta = data
            ? {
              participants: data.participants ?? {},
              project_name: data.project_name ?? "",
              address: data.address ?? "",
            }
            : null;
          const normalizeMediaUrl = (it) => {
            const u = typeof it === "string" ? it : it?.url;
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            const BASE = "http://localhost:8000";
            return `${BASE}${u.startsWith("/") ? "" : "/"}${u}`;
          };
          // 폼에서 쓰는 키만 추출 + id 유지
          const pick = (obj) => {
            if (!obj) return null;
            const toItem = (it) => ({
              id: typeof it === "object" && it !== null ? it.id : undefined,
              url: normalizeMediaUrl(it),
            });

            const cm = obj.participants?.["CM/감리단"] ?? "";
            const pm = obj.participants?.["PM단"] ?? "";
            const out = {
              id: obj.id ?? "",
              // 단일/배열 모두 지원
              birdseye_images: Array.isArray(obj.birdseye_images)
                ? obj.birdseye_images.map(toItem)
                : [],
              birdseye_image: obj.birdseye_image
                ? normalizeMediaUrl(obj.birdseye_image)
                : "",
              project_name: obj.project_name ?? "",
              client: obj.participants?.["발주처"] ?? "",
              CM_supervision: cm || pm,
              CM_supervision_role: cm ? "CM/감리단" : pm ? "PM단" : "CM/감리단",
              designer: obj.participants?.["설계사"] ?? "",
              constructor: obj.participants?.["시공사"] ?? "",
              types: obj.types ?? "",
              total_cost: obj.total_cost ?? "",
              contract_date: obj.contract_date ?? "",
              construction_date: obj.construction_date ?? "",
              estimated_completion_date: obj.estimated_completion_date ?? "",
              completion_date: obj.completion_date ?? "",
              site_location: obj.site_location ?? "",
              construction_area: obj.construction_area ?? "",
              business_scale: obj.business_scale ?? "",
            };
            return out;
          };

          // 조회 화면용 숫자 포맷터
          // - 3자리 콤마
          // - 소수부는 "존재하고 0이 아닐 때만" 최대 자리수까지 표시
          const formatNumForDisplay = (value, maxDecimals = 2) => {
            if (value === null || value === undefined || value === "")
              return "";
            const rawStr = String(value).replace(/,/g, "").trim();
            if (rawStr === "") return "";
            const num = Number(rawStr);
            if (Number.isNaN(num)) return String(value); // 숫자가 아니면 원본 그대로
            // 입력값 기준으로 소수부가 실질적으로 존재하는지 판단 (끝의 0 제거)
            const dec = (rawStr.split(".")[1] || "").replace(/0+$/, "");
            const decimalsToShow = Math.min(dec.length, maxDecimals);
            return new Intl.NumberFormat("ko-KR", {
              minimumFractionDigits: 0,
              maximumFractionDigits: decimalsToShow,
              useGrouping: true,
            }).format(num);
          };

          const form = pick(raw);
          if (form) {
            // 조회 화면에서만 가독성 있게 포맷
            form.total_cost = formatNumForDisplay(form.total_cost, 1); // 총사업비: 최대 1자리
          }
          setOverview(form ? { form, meta } : null);
        }
      } catch (e) {
        if (mounted) setOverview(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [projectId, token]);

  if (loading) {
    return <div className="p-6 text-slate-500">로딩 중...</div>;
  }

  // 진짜 없을 때만 등록 버튼
  if (!overview) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="bg-white rounded-xl  border border-slate-200 p-8 max-w-md text-center">
          <div className="text-title mb-2">등록된 사업개요가 없습니다</div>

          <div className="text-muted mb-6">
            사업개요를 먼저 등록하면 현장 개요 정보가 이곳에 표시됩니다.
          </div>

          {canManageOverview && (
            <button
              className="btn-create px-6 py-2"
              onClick={() =>
                navigate(`/project/${projectId}/overview/register`)
              }
            >
              사업개요 등록하기
            </button>
          )}
        </div>
      </div>
    );
  }

  // 있을 때는 조회 전용 렌더
  return (
    <div>
      <OverviewRegister
        is_superuser={is_superuser}
        canManageOverview={canManageOverview}
        initialForm={overview.form}
        readonly={true}
        viewActions={
          canManageOverview ? (
            <>
              <button
                className="btn-edit"
                onClick={() =>
                  navigate(`/project/${projectId}/overview/register/edit`, {
                    state: { initialForm: overview.form },
                  })
                }
              >
                수정
              </button>
              <button
                className="btn-delete"
                onClick={async () => {
                  if (!window.confirm("정말 삭제하시겠습니까?")) return;
                  try {
                    await api.delete(
                      `project/${projectId}/overview/${overview.form.id}/`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    alert("사업개요가 삭제되었습니다.");
                    setOverview(null);
                    navigate(`/project/${projectId}/overview/view`);
                  } catch {
                    alert("삭제 중 오류가 발생했습니다.");
                  }
                }}
              >
                삭제
              </button>
            </>
          ) : null
        }
      />

    </div>
  );
};

export default OverviewView;
